import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getSource } from '@/sources/registry';
import { getPref, setPref, PREF_KEYS } from '@/db/prefs';
import type { ComicDetails, PageRef, ReadGroup, ReadUnit } from '@/sources/types';
import { useSourceAuth } from '@/features/auth';

import { DEFAULT_PREFS, type ReaderFit, type ReaderMode, type ReaderPrefs, type ReaderState } from './types';
import { useFetcher } from './hooks/useFetcher';
import { useImagePreload } from './hooks/useImagePreload';
import { useKeyboardNav } from './hooks/useKeyboardNav';
import { recordPageRead } from '@/features/history';
import { cn } from '@/lib/utils';
import {
  copyDebugReport,
  getManualDebugReportText,
  recordDiagnostic,
  summarizeDiagnosticError,
} from '@/features/diagnostics';
import { DebugReportFallbackDialog } from '@/features/diagnostics/DebugReportFallbackDialog';
import { toast } from 'sonner';

import { ReaderToolbar } from './components/ReaderToolbar';
import { ProgressBar } from './components/ProgressBar';
import { SourceErrorBanner } from './components/SourceErrorBanner';
import { VerticalScroll } from './modes/VerticalScroll';
import { HorizontalSwipe } from './modes/HorizontalSwipe';
import { SpreadView } from './modes/SpreadView';

export interface ReaderProps {
  sourceId: string;
  comicId: string;
  readUnitId?: string;
  initialPage: number;
  /** Optional: tell the URL bar each time the active page changes. */
  onPageChange?: (page: number) => void;
}

const FIT_CYCLE: ReaderFit[] = ['width', 'height', 'free'];
const MODE_CYCLE: ReaderMode[] = ['vertical', 'horizontal', 'spread'];

const HISTORY_DWELL_MS = 30_000;

/**
 * The orchestrator component. Loads the gallery + page list, exposes a
 * resolve-on-demand URL cache, and delegates the actual layout to one of the
 * three mode components.
 *
 * Lifecycle:
 *   1. Mount: load `getComic` + `getPages` in parallel (each via its own
 *      key so they don't block each other when the source caches the page
 *      list separately).
 *   2. On every active-page change: enqueue the next N pages into the
 *      preload queue (`useImagePreload`).
 *   3. On 30s dwell or scroll-past: write a history row (`recordPageRead`).
 *   4. On mode/fit/direction change: persist to `db/prefs` (best-effort).
 *
 * The reader is intentionally source-agnostic: it only knows the
 * `ComicSource` contract.
 */
export function Reader({ sourceId, comicId, readUnitId, initialPage, onPageChange }: ReaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const source = useMemo(() => getSource(sourceId), [sourceId]);
  const { auth, ready: authReady } = useSourceAuth(source);

  const [currentPage, setCurrentPageState] = useState<number>(() => normalizePage(initialPage));
  const [prefs, setPrefsState] = useState<ReaderPrefs>(DEFAULT_PREFS);
  const [mobileChromeVisible, setMobileChromeVisible] = useState(false);
  const [manualDebugReportText, setManualDebugReportText] = useState<string | null>(null);

  // Load saved prefs once.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getPref<ReaderMode>(PREF_KEYS.READER_MODE),
      getPref<ReaderFit>('reader.fit'),
      getPref<'ltr' | 'rtl'>('reader.direction'),
      getPref<number>(PREF_KEYS.PRELOAD_COUNT),
    ]).then(([mode, fit, direction, preload]) => {
      if (cancelled) return;
      setPrefsState((cur) => ({
        mode: mode ?? cur.mode,
        fit: fit ?? cur.fit,
        direction: direction ?? cur.direction,
        preload: typeof preload === 'number' && preload > 0 ? preload : cur.preload,
      }));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const setPrefs = useCallback((next: ReaderPrefs) => {
    setPrefsState(next);
    void setPref(PREF_KEYS.READER_MODE, next.mode);
    void setPref('reader.fit', next.fit);
    void setPref('reader.direction', next.direction);
    void setPref(PREF_KEYS.PRELOAD_COUNT, next.preload);
  }, []);

  // Comic detail + pages list. We gate the fetcher keys on `authReady`
  // so the first request carries the user's saved cookies (otherwise
  // exhentai redirect-loops the worker and the SPA shows a 502).
  // `authReady` flips once on mount; the keys swap from `undefined`
  // (idle) to a stable string at that point, triggering a single fetch.
  const deferComicQueryUntilPages =
    sourceId === 'copy_manga' && readUnitId === undefined;
  const pagesKey =
    source && comicId && authReady ? `${sourceId}::pages::${comicId}::${readUnitId ?? ''}` : undefined;

  const pagesQuery = useFetcher<PageRef[]>(pagesKey, () => {
    if (!source) throw new Error(`Unknown source: ${sourceId}`);
    return source.getPages(comicId, auth, { readUnitId });
  });
  const comicKey =
    source && comicId && authReady && (!deferComicQueryUntilPages || pagesQuery.data)
      ? `${sourceId}::comic::${comicId}`
      : undefined;
  const comicQuery = useFetcher<ComicDetails>(comicKey, () => {
    if (!source) throw new Error(`Unknown source: ${sourceId}`);
    return source.getComic(comicId, auth);
  });
  useEffect(() => {
    if (!comicQuery.error) return;
    recordDiagnostic(
      'source',
      'reader-comic:error',
      { sourceId, comicId, error: summarizeDiagnosticError(comicQuery.error) },
      'error',
    );
  }, [comicId, comicQuery.error, sourceId]);
  useEffect(() => {
    if (!pagesQuery.error) return;
    recordDiagnostic(
      'source',
      'reader-pages:error',
      { sourceId, comicId, error: summarizeDiagnosticError(pagesQuery.error) },
      'error',
    );
  }, [comicId, pagesQuery.error, sourceId]);
  const selectedReadContext = useMemo(
    () => findReadContext(comicQuery.data?.readGroups, readUnitId),
    [comicQuery.data?.readGroups, readUnitId],
  );
  const selectedReadGroup = selectedReadContext?.group;
  const selectedReadUnit = selectedReadContext?.unit;
  const totalReadUnitCount = useMemo(
    () => countReadUnits(comicQuery.data?.readGroups),
    [comicQuery.data?.readGroups],
  );
  const selectedReadUnitIndex = selectedReadGroup && selectedReadUnit
    ? selectedReadGroup.units.findIndex((candidate) => candidate.id === selectedReadUnit.id)
    : -1;
  const selectedChapterPosition =
    selectedReadGroup && selectedReadUnitIndex >= 0 && (selectedReadGroup.units.length > 1 || totalReadUnitCount > 1)
      ? { current: selectedReadUnitIndex + 1, total: selectedReadGroup.units.length }
      : undefined;
  const previousReadUnit =
    selectedReadUnitIndex > 0 ? selectedReadGroup?.units[selectedReadUnitIndex - 1] : undefined;
  const nextReadUnit =
    selectedReadUnitIndex >= 0 ? selectedReadGroup?.units[selectedReadUnitIndex + 1] : undefined;

  // Per-page URL cache. Resolves on demand; preload writes here too.
  const urlCacheRef = useRef<Map<number, string>>(new Map());
  const urlInFlightRef = useRef<Map<number, Promise<string | undefined>>>(new Map());
  // Forces re-render on cache changes so PageImage receives a fresh resolver.
  const [urlCacheTick, setUrlCacheTick] = useState(0);

  useEffect(() => {
    urlCacheRef.current.clear();
    urlInFlightRef.current.clear();
    setUrlCacheTick((t) => t + 1);
  }, [sourceId, comicId, readUnitId]);

  const resolveUrl = useCallback(
    async (page: number): Promise<string | undefined> => {
      const cached = urlCacheRef.current.get(page);
      if (cached) return cached;
      const pending = urlInFlightRef.current.get(page);
      if (pending) return pending;
      const pages = pagesQuery.data;
      if (!source || !pages) return undefined;
      const ref = pages[page - 1];
      if (!ref) return undefined;
      const promise = (async () => {
        recordDiagnostic('reader', 'resolve-url:start', { sourceId, comicId, page });
        if (ref.resolvedUrl) {
          urlCacheRef.current.set(page, ref.resolvedUrl);
          setUrlCacheTick((t) => t + 1);
          recordDiagnostic('reader', 'resolve-url:pre-resolved', { sourceId, comicId, page });
          return ref.resolvedUrl;
        }
        try {
          const { url } = await source.resolveImageUrl(ref, auth);
          urlCacheRef.current.set(page, url);
          setUrlCacheTick((t) => t + 1);
          recordDiagnostic('reader', 'resolve-url:success', { sourceId, comicId, page });
          return url;
        } catch (err) {
          recordDiagnostic(
            'reader',
            'resolve-url:error',
            { sourceId, comicId, page, error: summarizeDiagnosticError(err) },
            'error',
          );
          throw err;
        }
      })();
      urlInFlightRef.current.set(page, promise);
      try {
        return await promise;
      } finally {
        if (urlInFlightRef.current.get(page) === promise) {
          urlInFlightRef.current.delete(page);
        }
      }
    },
    [source, pagesQuery.data, auth, sourceId, comicId],
  );

  const invalidateUrl = useCallback((page: number) => {
    urlCacheRef.current.delete(page);
    urlInFlightRef.current.delete(page);
    recordDiagnostic('reader', 'resolve-url:invalidate', { sourceId, comicId, page });
    setUrlCacheTick((t) => t + 1);
  }, [comicId, sourceId]);

  const getCachedUrl = useCallback(
    (page: number) => urlCacheRef.current.get(page),
    // urlCacheTick keeps consumers honest when the cache changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [urlCacheTick],
  );

  // Preload upcoming pages.
  const { enqueue: enqueuePreload, reset: resetPreload } = useImagePreload(3);
  useEffect(() => {
    resetPreload();
    const pages = pagesQuery.data;
    if (!pages) return;
    void (async () => {
      for (let i = 1; i <= prefs.preload; i++) {
        const target = currentPage + i;
        if (target > pages.length) break;
        try {
          const url = await resolveUrl(target);
          if (url) enqueuePreload(url);
        } catch {
          // best-effort; preload errors are silent
        }
      }
    })();
  }, [currentPage, prefs.preload, pagesQuery.data, enqueuePreload, resetPreload, resolveUrl]);

  // History writes — two triggers (dwell + scroll-past). We dedupe by
  // tracking the highest page already written for this session, since both
  // triggers fire repeatedly.
  const historyWrittenRef = useRef<number>(0);
  const dwellTimerRef = useRef<number | null>(null);

  const writeHistory = useCallback(
    (page: number) => {
      const comic = comicQuery.data;
      const pages = pagesQuery.data;
      if (!comic || !pages) return;
      if (page <= historyWrittenRef.current) return;
      historyWrittenRef.current = page;
      void recordPageRead({
        sourceId,
        comicId,
        readUnitId,
        readUnitTitle: selectedReadUnit?.title,
        readGroupId: selectedReadGroup?.id,
        readGroupTitle: selectedReadGroup?.title,
        page,
        totalPages: pages.length,
        titleSnapshot: comic.title,
        coverUrl: comic.coverUrl,
      });
    },
    [comicId, comicQuery.data, pagesQuery.data, readUnitId, selectedReadGroup?.id, selectedReadGroup?.title, selectedReadUnit?.title, sourceId],
  );

  useEffect(() => {
    if (dwellTimerRef.current) window.clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = window.setTimeout(() => {
      writeHistory(currentPage);
    }, HISTORY_DWELL_MS);
    return () => {
      if (dwellTimerRef.current) window.clearTimeout(dwellTimerRef.current);
    };
  }, [currentPage, writeHistory]);

  // setCurrentPage wrapper that also notifies the URL bar.
  const setCurrentPage = useCallback(
    (page: number) => {
      const pages = pagesQuery.data;
      if (!pages) {
        setCurrentPageState(normalizePage(page));
        return;
      }
      const clamped = clampPage(page, pages.length);
      setCurrentPageState(clamped);
      onPageChange?.(clamped);
    },
    [onPageChange, pagesQuery.data],
  );

  useEffect(() => {
    const pages = pagesQuery.data;
    const next = pages ? clampPage(initialPage, pages.length) : normalizePage(initialPage);
    setCurrentPageState((cur) => (cur === next ? cur : next));
  }, [initialPage, pagesQuery.data]);

  useEffect(() => {
    const pages = pagesQuery.data;
    if (!pages) return;
    const clamped = clampPage(currentPage, pages.length);
    if (clamped !== currentPage) setCurrentPage(clamped);
  }, [currentPage, pagesQuery.data, setCurrentPage]);

  // Keyboard nav handlers — wired to the active mode.
  const handlePrev = useCallback(() => {
    const delta = prefs.mode === 'spread' ? -2 : -1;
    setCurrentPage(currentPage + delta);
  }, [currentPage, prefs.mode, setCurrentPage]);
  const handleNext = useCallback(() => {
    const delta = prefs.mode === 'spread' ? 2 : 1;
    setCurrentPage(currentPage + delta);
    // For vertical, also mark the page just-scrolled-past.
    if (prefs.mode === 'vertical') writeHistory(currentPage);
  }, [currentPage, prefs.mode, setCurrentPage, writeHistory]);
  const handleFirst = useCallback(() => setCurrentPage(1), [setCurrentPage]);
  const handleLast = useCallback(() => {
    const pages = pagesQuery.data;
    if (pages) setCurrentPage(pages.length);
  }, [pagesQuery.data, setCurrentPage]);
  const handleCycleFit = useCallback(() => {
    const idx = FIT_CYCLE.indexOf(prefs.fit);
    const next = FIT_CYCLE[(idx + 1) % FIT_CYCLE.length]!;
    setPrefs({ ...prefs, fit: next });
  }, [prefs, setPrefs]);
  const handleCycleMode = useCallback(() => {
    const idx = MODE_CYCLE.indexOf(prefs.mode);
    const next = MODE_CYCLE[(idx + 1) % MODE_CYCLE.length]!;
    setPrefs({ ...prefs, mode: next });
  }, [prefs, setPrefs]);
  const handleToggleDirection = useCallback(() => {
    setPrefs({ ...prefs, direction: prefs.direction === 'ltr' ? 'rtl' : 'ltr' });
  }, [prefs, setPrefs]);
  const handleToggleMobileChrome = useCallback(() => {
    setMobileChromeVisible((visible) => !visible);
  }, []);
  const handleCopyDebugReport = useCallback(() => {
    void copyDebugReport({
      sourceId,
      comicId,
      page: currentPage,
      totalPages: pagesQuery.data?.length,
      readerMode: prefs.mode,
    })
      .then(() => toast.success('Debug report copied.'))
      .catch((err: unknown) => {
        const reportText = getManualDebugReportText(err);
        if (reportText) {
          setManualDebugReportText(reportText);
          return;
        }
        const message = err instanceof Error ? err.message : String(err);
        toast.error(`Could not copy debug report: ${message}`);
      });
  }, [comicId, currentPage, pagesQuery.data?.length, prefs.mode, sourceId]);
  const detailPath = `/source/${sourceId}/comic/${encodeURIComponent(comicId)}`;
  const readerPathForUnit = useCallback(
    (unitId: string) => {
      const params = new URLSearchParams(location.search);
      params.set('unit', unitId);
      return `/source/${sourceId}/comic/${encodeURIComponent(comicId)}/read/1?${params.toString()}`;
    },
    [comicId, location.search, sourceId],
  );
  const handlePreviousChapter = useCallback(() => {
    if (!previousReadUnit) return;
    navigate(readerPathForUnit(previousReadUnit.id), { replace: true, state: location.state });
  }, [location.state, navigate, previousReadUnit, readerPathForUnit]);
  const handleNextChapter = useCallback(() => {
    if (!nextReadUnit) return;
    navigate(readerPathForUnit(nextReadUnit.id), { replace: true, state: location.state });
  }, [location.state, navigate, nextReadUnit, readerPathForUnit]);
  const handleBackToGallery = useCallback(() => {
    navigate(detailPath, { replace: true, state: location.state });
  }, [detailPath, location.state, navigate]);
  const handleClose = useCallback(() => {
    handleBackToGallery();
  }, [handleBackToGallery]);

  useKeyboardNav({
    onPrev: handlePrev,
    onNext: handleNext,
    onFirst: handleFirst,
    onLast: handleLast,
    onCycleFit: handleCycleFit,
    onCycleMode: handleCycleMode,
    onToggleDirection: handleToggleDirection,
    onClose: handleClose,
    onHelp: () => undefined,
  });

  // Page-visible callback used by vertical mode to drive history "scroll past"
  // writes. The mode reports the page closest to the viewport center.
  const handlePageVisible = useCallback(
    (page: number) => {
      if (page === currentPage) return;
      setCurrentPage(page);
      // Mark prior page as read.
      if (page > currentPage) writeHistory(currentPage);
    },
    [currentPage, setCurrentPage, writeHistory],
  );

  if (!source) {
    return (
      <div className="container py-6">
        <SourceErrorBanner error={new Error(`No registered source with id "${sourceId}".`)} />
        <DebugReportFallbackDialog
          reportText={manualDebugReportText}
          onOpenChange={(open) => {
            if (!open) setManualDebugReportText(null);
          }}
        />
      </div>
    );
  }
  if (comicQuery.error) {
    return (
      <div className="container py-6">
        <SourceErrorBanner
          error={comicQuery.error}
          sourceId={sourceId}
          onRetry={() => comicQuery.refetch()}
          onCopyDebugReport={handleCopyDebugReport}
        />
        <DebugReportFallbackDialog
          reportText={manualDebugReportText}
          onOpenChange={(open) => {
            if (!open) setManualDebugReportText(null);
          }}
        />
      </div>
    );
  }
  if (pagesQuery.error) {
    return (
      <div className="container py-6">
        <SourceErrorBanner
          error={pagesQuery.error}
          sourceId={sourceId}
          onRetry={() => pagesQuery.refetch()}
          onCopyDebugReport={handleCopyDebugReport}
        />
        <DebugReportFallbackDialog
          reportText={manualDebugReportText}
          onOpenChange={(open) => {
            if (!open) setManualDebugReportText(null);
          }}
        />
      </div>
    );
  }
  if (comicQuery.loading || pagesQuery.loading || !comicQuery.data || !pagesQuery.data) {
    return (
      <div className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground">
        Loading reader…
      </div>
    );
  }

  const state: ReaderState = {
    comic: comicQuery.data,
    pages: pagesQuery.data,
    currentPage,
    setCurrentPage,
    resolveUrl,
    invalidateUrl,
    getCachedUrl,
    prefs,
    setPrefs,
    sourceId,
  };

  const hideMobileChrome = prefs.mode === 'horizontal' && !mobileChromeVisible;

  return (
    <div className="flex min-h-screen flex-col">
      <ReaderToolbar
        title={state.comic.title}
        currentPage={currentPage}
        totalPages={state.pages.length}
        chapterPosition={selectedChapterPosition}
        mode={prefs.mode}
        onCycleMode={handleCycleMode}
        onBackToGallery={handleBackToGallery}
        onCopyDebugReport={handleCopyDebugReport}
        onPreviousChapter={previousReadUnit ? handlePreviousChapter : undefined}
        onNextChapter={nextReadUnit ? handleNextChapter : undefined}
        previousChapterTitle={previousReadUnit?.title}
        nextChapterTitle={nextReadUnit?.title}
        className={cn(
          'transition-transform duration-200 md:translate-y-0',
          hideMobileChrome && '-translate-y-full md:translate-y-0',
        )}
      />

      <main className="flex-1">
        {prefs.mode === 'vertical' && (
          <VerticalScroll state={state} onPageVisible={handlePageVisible} />
        )}
        {prefs.mode === 'horizontal' && (
          <HorizontalSwipe
            state={state}
            onPageVisible={writeHistory}
            onToggleChrome={handleToggleMobileChrome}
          />
        )}
        {prefs.mode === 'spread' && (
          <SpreadView state={state} onPageVisible={writeHistory} />
        )}
      </main>

      <ProgressBar
        currentPage={currentPage}
        totalPages={state.pages.length}
        onJump={setCurrentPage}
        className={cn(
          'transition-transform duration-200 md:translate-y-0',
          hideMobileChrome && 'translate-y-full md:translate-y-0',
        )}
      />

      {hideMobileChrome && (
        <div className="pointer-events-none fixed inset-x-0 bottom-4 z-30 text-center text-[11px] text-muted-foreground md:hidden">
          Tap center to show controls
        </div>
      )}
      <DebugReportFallbackDialog
        reportText={manualDebugReportText}
        onOpenChange={(open) => {
          if (!open) setManualDebugReportText(null);
        }}
      />

    </div>
  );
}

function normalizePage(page: number): number {
  return Number.isFinite(page) ? Math.max(1, Math.floor(page)) : 1;
}

function clampPage(page: number, totalPages: number): number {
  return Math.max(1, Math.min(totalPages, normalizePage(page)));
}

function findReadContext(
  groups: ReadGroup[] | undefined,
  readUnitId: string | undefined,
): { group: ReadGroup; unit: ReadUnit } | undefined {
  if (!groups || groups.length === 0) return undefined;
  for (const group of groups) {
    const unit = readUnitId
      ? group.units.find((candidate) => candidate.id === readUnitId)
      : group.units[0];
    if (unit) return { group, unit };
  }
  return undefined;
}

function countReadUnits(groups: ReadGroup[] | undefined): number {
  return groups?.reduce((total, group) => total + group.units.length, 0) ?? 0;
}
